<template>
  <section class="dashboard-section" id="dashboard">
    <!-- Animated background layer -->
    <AnimatedBackground class="bg-layer" />

    <div class="bd-grid relative-content">
      <!-- Enhanced Title Section -->
      <div class="title-wrapper">
        <div class="title-badge">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="section-title">{{ $t('dashboard.title') }}</h2>
        <p class="section-subtitle">{{ $t('dashboard.subtitle') }}</p>
      </div>

      <div class="dashboard-container">
        <!-- Enhanced Form Section -->
        <div class="dashboard-form-wrapper">
          <div class="form-card">
            <div class="form-header">
              <div class="form-header-content">
                <h3 class="form-title">{{ $t('dashboard.form_title') }}</h3>
                <p class="form-description">{{ $t('dashboard.form_description') }}</p>
              </div>
            </div>

            <div class="dashboard-form">
              <!-- Income Input -->
              <div class="form-group">
                <label class="form-label" for="income">
                  {{ $t('dashboard.income_label') }}
                </label>
                <div class="input-wrapper">
                  <span class="input-icon">💰</span>
                  <input
                    id="income"
                    v-model.number="monthlyIncome"
                    type="number"
                    :placeholder="$t('dashboard.income_placeholder')"
                    class="form-input"
                    @input="calculateDistribution"
                    min="0"
                  >
                  <span class="input-suffix">COP</span>
                </div>
                <div v-if="monthlyIncome" class="input-helper">
                  <span class="helper-label">{{ $t('dashboard.annual') }}:</span>
                  <span class="helper-value">${{ formatCurrency(monthlyIncome * 12) }}</span>
                </div>
              </div>

              <!-- Financial Goal -->
              <div class="form-group">
                <label class="form-label" for="goal">
                  {{ $t('dashboard.goal_label') }}
                </label>
                <div class="select-wrapper">
                  <span class="input-icon">🎯</span>
                  <select
                    id="goal"
                    v-model="financialGoal"
                    class="form-select"
                    @change="calculateDistribution"
                  >
                    <option value="">{{ $t('dashboard.goal_placeholder') }}</option>
                    <option value="retirement">{{ $t('dashboard.goals.retirement') }}</option>
                    <option value="house">{{ $t('dashboard.goals.house') }}</option>
                    <option value="education">{{ $t('dashboard.goals.education') }}</option>
                    <option value="business">{{ $t('dashboard.goals.business') }}</option>
                    <option value="travel">{{ $t('dashboard.goals.travel') }}</option>
                    <option value="emergency">{{ $t('dashboard.goals.emergency') }}</option>
                    <option value="wealth">{{ $t('dashboard.goals.wealth') }}</option>
                  </select>
                </div>
              </div>

              <!-- Risk Profile -->
              <div class="form-group">
                <label class="form-label">
                  {{ $t('dashboard.risk_label') }}
                </label>
                <div class="risk-options">
                  <label
                    v-for="risk in ['conservative', 'moderate', 'aggressive']"
                    :key="risk"
                    class="risk-option"
                    :class="{ active: riskProfile === risk }"
                  >
                    <input
                      type="radio"
                      :value="risk"
                      v-model="riskProfile"
                      @change="calculateDistribution"
                      class="risk-input"
                      :aria-label="$t(`dashboard.risk_profiles.${risk}`)"
                    >
                    <span class="risk-content">
                      <span class="risk-icon">{{ getRiskIcon(risk) }}</span>
                      <span class="risk-name">{{ $t(`dashboard.risk_profiles.${risk}`) }}</span>
                      <span class="risk-desc">{{ getRiskDescription(risk) }}</span>
                    </span>
                  </label>
                </div>
              </div>

              <!-- Quick Tip -->
              <div v-if="showTip" class="quick-tip">
                <div class="tip-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="currentColor"/>
                  </svg>
                </div>
                <p class="tip-text">{{ currentTip }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Chart Section -->
        <div class="visualization-wrapper">
          <div class="chart-card">
            <!-- Stats Summary -->
            <div class="stats-summary">
              <div class="stat-item">
                <span class="stat-label">{{ $t('dashboard.total_distribution') }}</span>
                <span class="stat-value">100%</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('dashboard.active_categories') }}</span>
                <span class="stat-value">{{ activeCategoriesCount }}</span>
              </div>
            </div>

            <!-- Enhanced Pie Chart -->
            <div class="pie-chart-wrapper">
              <svg class="pie-chart" viewBox="0 0 240 240" width="320" height="320">
                <defs>
                  <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                    <feOffset dx="0" dy="2" result="offsetblur"/>
                    <feFlood flood-color="#000000" flood-opacity="0.1"/>
                    <feComposite in2="offsetblur" operator="in"/>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                <!-- Background circle -->
                <circle cx="120" cy="120" r="95" fill="#F8F9FA" opacity="0.5"/>
                <circle cx="120" cy="120" r="92" fill="none" stroke="#E6E8EC" stroke-width="1" stroke-dasharray="4 4" opacity="0.5"/>

                <!-- Pie segments -->
                <g class="pie-segments">
                  <path
                    v-for="(segment, index) in pieSegments"
                    :key="index"
                    :d="segment.path"
                    :fill="segment.color"
                    class="pie-segment"
                    :class="{ 'segment-hover': hoveredSegment === index }"
                    @mouseenter="hoveredSegment = index"
                    @mouseleave="hoveredSegment = null"
                    filter="url(#shadow)"
                  />
                </g>

                <!-- Center circle with info -->
                <circle cx="120" cy="120" r="50" fill="white" filter="url(#shadow)"/>
                <text x="120" y="110" text-anchor="middle" class="chart-center-label">
                  {{ $t('dashboard.total_monthly') }}
                </text>
                <text x="120" y="135" text-anchor="middle" class="chart-center-amount">
                  ${{ formatCurrency(monthlyIncome || 0) }}
                </text>

                <!-- Percentage labels -->
                <g class="segment-labels">
                  <text
                    v-for="(segment, index) in pieSegments"
                    :key="'label-' + index"
                    :x="segment.labelX"
                    :y="segment.labelY"
                    text-anchor="middle"
                    class="segment-label"
                    :class="{ 'label-active': hoveredSegment === index }"
                  >
                    {{ segment.percentage }}%
                  </text>
                </g>
              </svg>
            </div>

            <!-- Enhanced Legend -->
            <div class="chart-legend">
              <div class="legend-header">
                <h4 class="legend-title">{{ $t('dashboard.distribution') }}</h4>
                <button class="legend-action" @click="resetDistribution">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 12V9C4 5.68629 6.68629 3 10 3H14C17.3137 3 20 5.68629 20 9V12M4 12L7 9M4 12L7 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ $t('dashboard.reset_button') }}
                </button>
              </div>
              
              <div class="legend-items">
                <div
                  v-for="(category, index) in categories"
                  :key="category.key"
                  class="legend-item"
                  :class="{ 'legend-item-active': hoveredSegment === index }"
                  @mouseenter="hoveredSegment = index"
                  @mouseleave="hoveredSegment = null"
                >
                  <div class="legend-indicator">
                    <div class="legend-color" :style="{ backgroundColor: category.color }"></div>
                    <div class="legend-info">
                      <span class="legend-label">{{ $t(`dashboard.categories.${category.key}`) }}</span>
                      <span class="legend-percentage">{{ distribution[category.key] }}%</span>
                    </div>
                  </div>
                  <div class="legend-amount">
                    ${{ formatCurrency(getAmount(category.key)) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Insights Section -->
            <div v-if="hasCompleteData" class="insights-section">
              <div class="insights-header">
                <svg class="insights-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.09 8.26L19 7L15.45 11.82L21 16L14.5 16.73L13.09 23L12 17L10.91 23L9.5 16.73L3 16L8.55 11.82L5 7L10.91 8.26L12 2Z" fill="currentColor" opacity="0.2"/>
                  <path d="M12 2L13.09 8.26L19 7L15.45 11.82L21 16L14.5 16.73L13.09 23L12 17L10.91 23L9.5 16.73L3 16L8.55 11.82L5 7L10.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <h4 class="insights-title">{{ $t('dashboard.insights') }}</h4>
              </div>
              <div class="insights-grid">
                <div v-for="insight in getInsights()" :key="insight" class="insight-card">
                  <span class="insight-icon">✓</span>
                  <p class="insight-text">{{ insight }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'DistributionDashboard',
  data() {
    return {
      monthlyIncome: '',
      financialGoal: '',
      riskProfile: '',
      hoveredSegment: null,
      showTip: false,
      currentTip: '',
      distribution: {
        emergency: 0,
        savings: 0,
        expenses: 0,
        debts: 0,
        investments: 0
      },
      categories: [
        { key: 'emergency', color: '#ff6b35' },
        { key: 'savings', color: '#06b6d4' },
        { key: 'expenses', color: '#f59e0b' },
        { key: 'debts', color: '#ef4444' },
        { key: 'investments', color: '#8b5cf6' }
      ],
      tips: []
    }
  },
  computed: {
    hasCompleteData() {
      return this.monthlyIncome && this.financialGoal && this.riskProfile;
    },
    activeCategoriesCount() {
      return Object.values(this.distribution).filter(v => v > 0).length;
    },
    // Replace the pieSegments computed property with this version:

    pieSegments() {
      const segments = [];
      let currentAngle = -90; // Start from top
      const centerX = 120;
      const centerY = 120;
      const radius = 90;
      const innerRadius = 50;

      // Changed from forEach((category, index) => ...) to forEach((category) => ...)
      // since index was not being used
      this.categories.forEach((category) => {
        const percentage = this.distribution[category.key];
        if (percentage <= 0) return;

        const angle = (percentage / 100) * 360;
        const startAngleRad = (currentAngle * Math.PI) / 180;
        const endAngleRad = ((currentAngle + angle) * Math.PI) / 180;

        // Create donut segment path
        const x1 = centerX + radius * Math.cos(startAngleRad);
        const y1 = centerY + radius * Math.sin(startAngleRad);
        const x2 = centerX + radius * Math.cos(endAngleRad);
        const y2 = centerY + radius * Math.sin(endAngleRad);
        const x3 = centerX + innerRadius * Math.cos(endAngleRad);
        const y3 = centerY + innerRadius * Math.sin(endAngleRad);
        const x4 = centerX + innerRadius * Math.cos(startAngleRad);
        const y4 = centerY + innerRadius * Math.sin(startAngleRad);

        const largeArc = angle > 180 ? 1 : 0;

        const path = `
          M ${x1} ${y1}
          A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
          L ${x3} ${y3}
          A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
          Z
        `;

        // Calculate label position
        const midAngle = currentAngle + angle / 2;
        const midAngleRad = (midAngle * Math.PI) / 180;
        const labelRadius = 70;
        const labelX = centerX + labelRadius * Math.cos(midAngleRad);
        const labelY = centerY + labelRadius * Math.sin(midAngleRad) + 5;

        segments.push({
          color: category.color,
          path: path.trim(),
          labelX,
          labelY,
          percentage
        });

        currentAngle += angle;
      });

      return segments;
    }
  },
  mounted() {
    this.initializeTips();
    this.showRandomTip();
  },
  watch: {
    '$i18n.locale'() {
      this.initializeTips();
      this.showRandomTip();
    }
  },
  methods: {
    initializeTips() {
      this.tips = [
        this.$t('dashboard.tips.t1'),
        this.$t('dashboard.tips.t2'),
        this.$t('dashboard.tips.t3'),
        this.$t('dashboard.tips.t4')
      ];
    },
    calculateDistribution() {
      if (!this.monthlyIncome || !this.financialGoal || !this.riskProfile) {
        this.distribution = { emergency: 0, savings: 0, expenses: 0, debts: 0, investments: 0 };
        return;
      }

      let base = { emergency: 15, savings: 20, expenses: 50, debts: 10, investments: 5 };

      // Adjust based on financial goal
      switch (this.financialGoal) {
        case 'retirement': base = { emergency: 10, savings: 15, expenses: 45, debts: 10, investments: 20 }; break;
        case 'house': base = { emergency: 10, savings: 30, expenses: 40, debts: 15, investments: 5 }; break;
        case 'education': base = { emergency: 10, savings: 25, expenses: 45, debts: 15, investments: 5 }; break;
        case 'business': base = { emergency: 15, savings: 15, expenses: 40, debts: 5, investments: 25 }; break;
        case 'travel': base = { emergency: 10, savings: 25, expenses: 45, debts: 10, investments: 10 }; break;
        case 'emergency': base = { emergency: 25, savings: 15, expenses: 40, debts: 15, investments: 5 }; break;
        case 'wealth': base = { emergency: 10, savings: 25, expenses: 35, debts: 0, investments: 30 }; break;
      }

      // Adjust based on risk profile
      switch (this.riskProfile) {
        case 'conservative':
          base.emergency = Math.min(30, base.emergency + 5);
          base.savings = Math.min(35, base.savings + 5);
          base.investments = Math.max(0, base.investments - 10);
          break;
        case 'moderate':
          base.investments = Math.min(25, base.investments + 5);
          base.expenses = Math.max(35, base.expenses - 5);
          break;
        case 'aggressive':
          base.investments = Math.min(35, base.investments + 15);
          base.savings = Math.max(10, base.savings - 5);
          base.emergency = Math.max(10, base.emergency - 5);
          base.expenses = Math.max(30, base.expenses - 5);
          break;
      }

      // Normalize to 100%
      const total = Object.values(base).reduce((s, v) => s + v, 0);
      Object.keys(base).forEach(k => { base[k] = Math.round((base[k] / total) * 100); });
      
      const finalTotal = Object.values(base).reduce((s, v) => s + v, 0);
      if (finalTotal !== 100) {
        const maxKey = Object.keys(base).reduce((a, b) => base[a] > base[b] ? a : b);
        base[maxKey] += 100 - finalTotal;
      }

      this.distribution = base;
      this.showRandomTip();
    },
    getAmount(category) {
      if (!this.monthlyIncome) return 0;
      return Math.round((this.monthlyIncome * this.distribution[category]) / 100);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat(this.$i18n.locale === 'es' ? 'es-CO' : 'en-US', {
        style: 'decimal',
        maximumFractionDigits: 0
      }).format(amount);
    },
    getRiskIcon(risk) {
      const icons = { conservative: '🛡️', moderate: '⚖️', aggressive: '🚀' };
      return icons[risk];
    },
    getRiskDescription(risk) {
      return this.$t(`dashboard.risk_descriptions.${risk}`);
    },
    getInsights() {
      const insights = [];
      if (this.distribution.emergency < 10) insights.push(this.$t('dashboard.insights_list.raise_emergency'));
      if (this.distribution.investments > 20) insights.push(this.$t('dashboard.insights_list.diversify'));
      if (this.distribution.debts > 15) insights.push(this.$t('dashboard.insights_list.pay_high_interest'));
      if (this.distribution.savings > 25) insights.push(this.$t('dashboard.insights_list.set_specific_goals'));
      return insights.slice(0, 3);
    },
    showRandomTip() {
      this.currentTip = this.tips[Math.floor(Math.random() * this.tips.length)];
      this.showTip = true;
    },
    resetDistribution() {
      this.monthlyIncome = '';
      this.financialGoal = '';
      this.riskProfile = '';
      this.distribution = { emergency: 0, savings: 0, expenses: 0, debts: 0, investments: 0 };
      this.showRandomTip();
    }
  }
}
</script>

<style scoped src="@/styles/Distro-Dashboard.css"></style>